import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

export interface Watchlist {
  id: string
  name: string
  symbols: string[]
  createdAt: number
}

interface WatchlistStore {
  watchlists: Watchlist[]
  activeId: string | null
  // Actions
  createWatchlist: (name: string) => void
  deleteWatchlist: (id: string) => void
  renameWatchlist: (id: string, name: string) => void
  setActive: (id: string) => void
  addSymbol: (watchlistId: string, symbol: string) => void
  removeSymbol: (watchlistId: string, symbol: string) => void
  reorderSymbol: (watchlistId: string, fromIdx: number, toIdx: number) => void
  importWatchlist: (name: string, symbols: string[]) => void
}

function makeId(): string {
  return `wl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

const DEFAULT_WATCHLIST: Watchlist = {
  id: 'wl-default',
  name: 'My Watchlist',
  symbols: ['BTC', 'ETH', 'RELIANCE.NS', 'AAPL', '^NSEI'],
  createdAt: Date.now(),
}

export const useWatchlistStore = create<WatchlistStore>()(
  persist(
    immer((set) => ({
      watchlists: [DEFAULT_WATCHLIST],
      activeId: DEFAULT_WATCHLIST.id,

      createWatchlist: (name) =>
        set((s) => {
          const wl: Watchlist = { id: makeId(), name, symbols: [], createdAt: Date.now() }
          s.watchlists.push(wl)
          s.activeId = wl.id
        }),

      deleteWatchlist: (id) =>
        set((s) => {
          s.watchlists = s.watchlists.filter((w) => w.id !== id)
          if (s.activeId === id) s.activeId = s.watchlists[0]?.id ?? null
        }),

      renameWatchlist: (id, name) =>
        set((s) => {
          const wl = s.watchlists.find((w) => w.id === id)
          if (wl) wl.name = name
        }),

      setActive: (id) =>
        set((s) => {
          s.activeId = id
        }),

      addSymbol: (watchlistId, symbol) =>
        set((s) => {
          const wl = s.watchlists.find((w) => w.id === watchlistId)
          if (wl && !wl.symbols.includes(symbol)) wl.symbols.push(symbol)
        }),

      removeSymbol: (watchlistId, symbol) =>
        set((s) => {
          const wl = s.watchlists.find((w) => w.id === watchlistId)
          if (wl) wl.symbols = wl.symbols.filter((s) => s !== symbol)
        }),

      reorderSymbol: (watchlistId, fromIdx, toIdx) =>
        set((s) => {
          const wl = s.watchlists.find((w) => w.id === watchlistId)
          if (!wl || fromIdx === toIdx) return
          const [item] = wl.symbols.splice(fromIdx, 1)
          wl.symbols.splice(toIdx, 0, item)
        }),

      importWatchlist: (name, symbols) =>
        set((s) => {
          const wl: Watchlist = { id: makeId(), name, symbols: [...symbols], createdAt: Date.now() }
          s.watchlists.push(wl)
          s.activeId = wl.id
        }),
    })),
    { name: 'mhp-watchlists' }
  )
)
